import { QueryClient, VueQueryPlugin } from '@tanstack/vue-query'
import type { App, Plugin } from 'vue'
import { FetchError } from '@/modules/core/composables/useFetch'

// The app-wide server-state layer. Doctrine:
//   server state (projections, lists, anything the API owns) → Query cache
//   client state (auth, modals, route mirrors, UI toggles)     → Pinia
// One QueryClient for the whole app; imperative cache access
// (optimistic patches, count reconciliation) imports this instance.

// Retry only what retrying can fix: 5xx and network failures. A 4xx will
// 4xx again, and the 401 path already throws a session-expired Error that
// should surface, not spin.
const retryPredicate = (failureCount: number, error: unknown): boolean => {
  if (error instanceof FetchError) return error.status >= 500 && failureCount < 2
  // fetch() rejects with TypeError on network-level failure.
  if (error instanceof TypeError) return failureCount < 2
  return false
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: retryPredicate,
      },
      mutations: {
        // Commands are non-idempotent writes; never auto-retry.
        retry: false,
      },
    },
  })
}

export const queryClient = createQueryClient()

// Installed after Pinia in main.ts: fetchers read auth/notification stores
// at request time, which requires an active Pinia instance.
export const queryPlugin: Plugin = {
  install(app: App) {
    app.use(VueQueryPlugin, { queryClient })
  },
}
